import React, { memo, Suspense } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { cn, css } from '@lyearn/style';
import { Stack } from '@lyearn/ui';
import Form, { FieldOverrides } from '@/components/Form';
import Loader from '@/components/Loader';
import { Dialog } from '@/components/Modals';

import useCreateClient from './hooks/useCreateClient';
import { CreateClientFormValueType, CreateClientModalProps } from './types';
import { LAYOUT } from './utils';

const OVERRIDES: FieldOverrides = {
  Section: {
    spacing: 'space-y-40',
    Name: {
      variant: 'heading-s',
    },
    Description: {
      variant: 'body-short-02',
      color: 'text-text-secondary',
    },
    Header: {
      className: 'mb-40 ms-8 space-y-4',
    },
  },
  spacing: {
    row: 'space-y-24',
  },
};

const dialogSize = css`
  max-width: 52rem;
  height: 68rem;
`;

const CreateClientModal: React.FC<CreateClientModalProps> = (props) => {
  const {
    submitRef,
    fetching,
    fields,
    setSubmitRef,
    validate,
    onSubmit,
    onClose,
    initialValues,
    isEditing,
  } = useCreateClient(props);
  const { t } = useTranslation('Clients');

  return (
    <Dialog
      open
      action={{
        disabled: fetching,
        isSubmitting: fetching,
        text: isEditing
          ? t('Common:Actions.SaveChanges')
          : t('Clients:ClientEditorModal.SaveClient'),
        onSubmit: submitRef,
        buttonSize: 'large',
      }}
      className="h-full"
      classes={{ paper: cn('px-0 w-full rounded-16', dialogSize) }}
      closeButton={{
        size: 'large',
        className: 'end-12 top-20',
      }}
      header={{
        title: isEditing
          ? t('Clients:ClientEditorModal.EditTitle')
          : t('Clients:ClientEditorModal.CreateTitle'),
        description: isEditing
          ? t('Clients:ClientEditorModal.EditDescription')
          : t('Clients:ClientEditorModal.CreateDescription'),
        variants: {
          description: 'body-short-01',
        },
        className: 'pt-28 pb-24',
      }}
      onClose={onClose}>
      <Stack className="flex-1 px-24 py-12 border-t border-b border-divider" direction="col">
        <Suspense fallback={<Loader className="mx-auto my-auto" />}>
          <Form<CreateClientFormValueType>
            fields={fields}
            formName="CreateClient"
            initialValues={initialValues}
            layout={LAYOUT}
            overrides={OVERRIDES}
            setSubmitRef={setSubmitRef}
            validate={validate}
            onSubmit={onSubmit}
          />
        </Suspense>
      </Stack>
    </Dialog>
  );
};

export default memo(CreateClientModal);
